import { useCallback, useEffect, useRef, useState } from "react";

// Web Speech API dictation. Only one field listens at a time; `listening`
// holds the active field key (or null). start(key, baseText, onText) appends
// recognised speech to baseText and streams it back via onText.
export function useDictation() {
  const [listening, setListening] = useState(null);
  const recRef = useRef(null);
  const SR =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = useCallback(() => {
    try {
      recRef.current?.stop();
    } catch (e) {}
    recRef.current = null;
    setListening(null);
  }, []);

  const start = useCallback(
    (key, baseText, onText) => {
      if (!SR) return;
      if (recRef.current) {
        stop();
        if (listening === key) return;
      }
      const rec = new SR();
      rec.lang = "en-AU";
      rec.continuous = true;
      rec.interimResults = true;
      let finalText = "";
      const prefix = baseText ? baseText.replace(/\s+$/, "") + " " : "";
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) finalText += t;
          else interim += t;
        }
        onText(prefix + finalText + interim);
      };
      rec.onend = () => {
        recRef.current = null;
        setListening(null);
      };
      rec.onerror = () => {
        recRef.current = null;
        setListening(null);
      };
      recRef.current = rec;
      try {
        rec.start();
        setListening(key);
      } catch (e) {
        setListening(null);
      }
    },
    [SR, listening, stop]
  );

  useEffect(() => () => stop(), [stop]);

  return { supported: !!SR, listening, start, stop };
}
